import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, navigate } from 'gatsby';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import getBreadcrumbSchema from '@repo/utils/getBreadcrumbSchema';
import getCollectionSchema from '@repo/utils/getCollectionSchema';
import getResizedProductImage from '@repo/utils/getResizedProductImage';

import CollectionHeader from '@repo/ui-components/collections/Header';
import CollectionHeaderFilters from '@repo/ui-components/collections/HeaderFilters';
import Container from '@repo/ui-components/collections/Container';
import ContainerStyles from '@repo/ui-components/collections/ContainerStyles';
import ContentContainer from '@repo/ui-components/collections/ContentContainer';
import DescriptionArea from '@repo/ui-components/collections/DescriptionArea';
import FilterBox from '@repo/ui-components/collections/FilterBox';
import Head from '@repo/ui-components/Head';
import PaginationArea from '@repo/ui-components/collections/PaginationArea';
import ProductCard from '@repo/ui-components/product-card/ProductCard';
import ProductCardGrid from '@repo/ui-components/ProductCardGrid';
import SelectSearch from '@repo/ui-components/collections/SelectSearch';
import Sidebar from '@repo/ui-components/collections/Sidebar';
import SidebarRow from '@repo/ui-components/collections/SidebarRow';
import SortByBox from '@repo/ui-components/collections/SortByBox';
import Title from '@repo/ui-components/collections/Title';

import Layout from '@components/layout';
import { styles } from '@utils';
import Breadcrumb from '@components/breadcrumb';
import parse from 'html-react-parser';

const getSelectedOptionValue = element =>
  element.options[element.options.selectedIndex].value;

const CollectionsPage = ({ pageContext }) => {
  const {
    skip,
    humanPageNumber,
    products,
    handle,
    limit,
    previousPagePath,
    nextPagePath,
  } = pageContext;

  const pagedProducts = products.slice(skip, humanPageNumber * limit);
  const [isBrowser, setIsBrowser] = useState(false);

  const seo = pageContext.categorySeo;

  const getFilter = () => {
    if (isBrowser) {
      const filter = window.localStorage.getItem('filterValue');
      if (window.location.pathname.includes(filter)) {
        return filter;
      }
      return '';
    }
    return '';
  };

  const [filterValue, setFilterValue] = useState(getFilter);

  const handleSortingFilter = event => {
    const filter = getSelectedOptionValue(event.target);
    setFilterValue(filter);
    navigate(`/${handle}/${filter}`);
  };

  const navigateOnChange = event => {
    navigate(getSelectedOptionValue(event.target));
  };

  useEffect(() => {
    if (typeof window !== `undefined`) {
      setIsBrowser(true);
    }
    localStorage.setItem('filterValue', filterValue);
  }, [filterValue]);

  useEffect(() => {
    if (typeof window !== `undefined`) {
      // don't use isBrowser useState as it breaks loading BV script
      const bvScriptTag = document.createElement('script');

      bvScriptTag.src = process.env.GATSBY_BAZAARVOICE_SCRIPT;
      bvScriptTag.key = 'bazaarvoice';
      bvScriptTag.defer = true;

      bvScriptTag.addEventListener('load', () => {
        const bvRatingObjects = [
          ...document.querySelectorAll('.ratings-container'),
        ].map(container => ({
          id: container.dataset.bazaarvoiceId,
          slug: container.dataset.bazaarvoiceSlug,
        }));
        const bvProducts = {};
        bvRatingObjects.forEach(product => {
          bvProducts[product.id] = {
            url: `${process.env.GATSBY_KURGO_BASE_URL}/${product.slug}`,
          };
        });

        if (bvRatingObjects.length > 0) {
          window.$BV?.ui('rr', 'inline_ratings', {
            productIds: bvProducts,
            containerPrefix: 'BVRRInlineRating',
          });
        }
      });
      document.body.appendChild(bvScriptTag);
    }
  }, []);

  return (
    <Layout
      headTagData={{
        meta: createOpenGraphTags(
          {
            title: seo?.title || pageContext.categoryName,
            description: seo?.description || '',
            url: `/${pageContext.handle}`,
          },
          'kurgo'
        ),
      }}
    >
      <Head
        title={seo?.title || pageContext.categoryName}
        description={seo?.description || ''}
        scriptTags={[
          <script type="application/ld+json" key="breadcrumb-schema">
            {JSON.stringify(getBreadcrumbSchema(pageContext.breadcrumbs))}
          </script>,
          <script type="application/ld+json" key="collection-schema">
            {JSON.stringify(getCollectionSchema(pageContext, pagedProducts))}
          </script>,
        ]}
      />
      <ContainerStyles backgroundColor={styles.colors.gray[100]}>
        <Breadcrumb elements={pageContext.breadcrumbs} />

        <ContentContainer>
          <Title>{pageContext.categoryName}</Title>
        </ContentContainer>

        <Container backgroundColor="white">
          <Sidebar active={pageContext.sideBarLinks?.length > 0}>
            <h5>Go to</h5>
            {pageContext.sideBarLinks?.map(entry => (
              <SidebarRow key={entry.text}>
                <Link to={entry.link}>{entry.text}</Link>
              </SidebarRow>
            ))}
          </Sidebar>

          <ContentContainer>
            <CollectionHeader>
              <CollectionHeaderFilters name="product-filter">
                <FilterBox
                  onChange={navigateOnChange}
                  active={pageContext.sideBarLinks?.length > 0}
                >
                  <option>Go to</option>
                  {pageContext.sideBarLinks?.map(entry => (
                    <option key={entry.text} value={entry.link}>
                      {entry.text}
                    </option>
                  ))}
                </FilterBox>
                <SortByBox>
                  <span>Sort by:</span>
                </SortByBox>
                <SelectSearch
                  onChange={handleSortingFilter}
                  value={filterValue}
                >
                  <option value="">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="atoz">A to Z</option>
                  <option value="ztoa">Z to A</option>
                  <option value="priceAsc">Price: Asc</option>
                  <option value="priceDesc">Price: Des</option>
                </SelectSearch>
              </CollectionHeaderFilters>
            </CollectionHeader>

            <ProductCardGrid padding="0">
              {pagedProducts?.map(product => {
                // eslint-disable-next-line no-param-reassign
                product.shopifyId = product.shopifyId.replace(
                  'gid://shopify/Product/',
                  ''
                );
                return (
                  <ProductCard
                    key={product.title}
                    product={{
                      ...product,
                      image: {
                        src: getResizedProductImage(
                          product?.featuredImage?.src
                        ),
                        altText:
                          product?.featuredImage?.altText || product.title,
                      },
                      sku: product.variants[0].sku,
                      price: product.variants[0].price,
                    }}
                    showRatings={false}
                  />
                );
              })}
            </ProductCardGrid>

            {(previousPagePath || nextPagePath) && (
              <PaginationArea
                previousPagePath={previousPagePath}
                nextPagePath={nextPagePath}
              />
            )}

            {pageContext?.categoryDescription && (
              <DescriptionArea>
                {parse(pageContext.categoryDescription)}
              </DescriptionArea>
            )}
          </ContentContainer>
        </Container>
      </ContainerStyles>
    </Layout>
  );
};

CollectionsPage.propTypes = {
  pageContext: PropTypes.shape({
    nextPagePath: PropTypes.string,
    previousPagePath: PropTypes.string,
    skip: PropTypes.number,
    humanPageNumber: PropTypes.number,
    limit: PropTypes.number,
    handle: PropTypes.string.isRequired,
    categorySeo: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        link: PropTypes.string,
      })
    ).isRequired,
    products: PropTypes.arrayOf(PropTypes.shape({})),
    categoryName: PropTypes.string.isRequired,
    categoryDescription: PropTypes.string.isRequired,
    sideBarHeading: PropTypes.string.isRequired,
    sideBarLinks: PropTypes.arrayOf(
      PropTypes.shape({ text: PropTypes.string, link: PropTypes.string })
        .isRequired
    ),
  }).isRequired,
};

export default CollectionsPage;
